import { useEffect, useState } from 'react';
import { Input, FormControl, FormLabel, FormErrorMessage } from '@chakra-ui/react';
import { Button, Text } from '@mercator-gcp/grout';
import { OAuthProvider, signInWithPopup } from 'firebase/auth';
import { useForm } from 'react-hook-form';
import GlobalLoader from 'components/elements/GlobalLoader';
import SeparatorWithText from 'components/elements/SeparatorWithText';
import SSOButton from 'components/elements/SSOButton';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import BasePageWithTopNav from 'components/layout/TopNavigationLayout';
import PageMetadata from 'components/PageMetadata';
import { auth } from 'firebaseConfig';
import { useNewUserRegistration } from 'hooks/useAuth';
import { useToast } from 'hooks/useToast';
import { useUserFromInviteCode } from 'hooks/useUserSignup';
import { useInviteCodeQuery } from 'state/queryParams';
import { trackPageView } from 'utils/analytics';
import { trackRegisterWithSSO } from 'utils/analytics/ssoAuth';
import { SSO_PROVIDERS } from 'utils/constants/ssoProviders';

type EmailSignUpFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  jobTitle: string;
  companyName: string;
};

const signUpContainer = {
  alignItems: 'center',
  justifyContent: 'center',
};

const heading = {
  pb: 'padding_24',
};

const inputContainer = {
  justifyContent: 'space-around',
  gap: 'padding_16',
};

const bodyStyle = {
  justifyContent: 'center',
  alignItems: 'center',
  display: 'flex',
};

const containerStyle = {
  paddingTop: 'padding_48',
  paddingBottom: 'padding_48',
  maxWidth: '435px',
  width: '100%',
  justifyContent: 'center',
  margin: 'auto',
};

const parentColumn = {
  gap: 'padding_16',
  width: '100%',
};

const dividerStyle = {
  mt: 'padding_24',
};

const matchEmailTextStyle = {
  textAlign: 'center',
  marginBottom: 'padding_16',
};

const SIGN_UP_WITH_MICROSOFT_TEXT = 'Sign up with Microsoft';

const SignUp = () => {
  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<EmailSignUpFormValues>();
  const { renderCustomToast: toast } = useToast();
  const inviteCodeQuery = useInviteCodeQuery();
  const { userInfoFromInviteCode } = useUserFromInviteCode(inviteCodeQuery);
  const { registerInvitedUser, registerInvitedUserWithMicrosoftSSO } =
    useNewUserRegistration();
  const [isMicrosoftLoading, setIsMicrosoftLoading] = useState<boolean>(false);

  /*
   * When we land on this page,
   * if user has an invitehash, then show loading state, until we fetch result.
   * If the result is 404, we redirect to login and show error.
   *
   * Otherwise we edit the form.
   */

  useEffect(() => {
    trackPageView({ sectionName: 'sign_up', subSectionName: 'sign_up_form' });
  }, []);

  const signupInviteUser = async (data: EmailSignUpFormValues) => {
    if (!inviteCodeQuery) {
      return;
    }

    await registerInvitedUser.mutateAsync({
      inviteCode: inviteCodeQuery,
      email: data.email,
      password: data.password,
      firstName: data.firstName,
      lastName: data.lastName,
      jobTitle: data.jobTitle,
    });
  };

  const onSubmit = async (data: EmailSignUpFormValues) => {
    if (registerInvitedUser.isLoading) {
      return;
    }
    if (data.password !== data.confirmPassword) {
      toast({
        title: 'Password Error',
        description: 'Passwords do not match',
        status: 'error',
        duration: 9000,
        isClosable: true,
      });

      return;
    }

    if (inviteCodeQuery) {
      signupInviteUser(data);
    }
  };

  const handleMicrosoftSSO = async () => {
    if (!inviteCodeQuery || registerInvitedUserWithMicrosoftSSO.isLoading) {
      return;
    }
    try {
      setIsMicrosoftLoading(true);
      trackRegisterWithSSO({
        provider: SSO_PROVIDERS.MICROSOFT,
        clickAction: 'start',
        clickText: SIGN_UP_WITH_MICROSOFT_TEXT,
      });

      const microsoftProvider = new OAuthProvider('microsoft.com');
      const result = await signInWithPopup(auth, microsoftProvider);
      const ssoToken = await result.user.getIdToken();
      await registerInvitedUserWithMicrosoftSSO.mutateAsync({
        inviteCode: inviteCodeQuery,
        accessToken: ssoToken,
        refreshToken: result.user.refreshToken,
        firstName: getValues('firstName'),
        lastName: getValues('lastName'),
        jobTitle: getValues('jobTitle'),
      });
      trackRegisterWithSSO({
        provider: SSO_PROVIDERS.MICROSOFT,
        clickAction: 'success',
        clickText: SIGN_UP_WITH_MICROSOFT_TEXT,
      });
    } catch (_) {
      toast({
        title: 'Microsoft sign-up failed',
        description: 'Please try again.',
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
      trackRegisterWithSSO({
        provider: SSO_PROVIDERS.MICROSOFT,
        clickAction: 'failure',
        clickText: SIGN_UP_WITH_MICROSOFT_TEXT,
      });
    } finally {
      setIsMicrosoftLoading(false);
    }
  };

  const validateStringValue = (
    value: string,
    minLength: number = 0,
    message: string = 'Invalid',
  ) => {
    const parsedValue = value.replace(/ /g, '');
    if (!parsedValue || parsedValue.length < minLength) {
      return message;
    }

    return true;
  };

  useEffect(() => {
    if (userInfoFromInviteCode.isSuccess) {
      const { email, first_name, last_name, job_title, company_name } =
        userInfoFromInviteCode.data;
      setValue('email', email);
      setValue('firstName', first_name);
      setValue('lastName', last_name);
      setValue('jobTitle', job_title);
      setValue('companyName', company_name);
    }
  }, [userInfoFromInviteCode.data, userInfoFromInviteCode.isSuccess]);

  if (inviteCodeQuery && !userInfoFromInviteCode.isSuccess) {
    return <GlobalLoader />;
  }

  return (
    <BasePageWithTopNav sx={bodyStyle}>
      <PageMetadata title="Sign Up" />
      <FlexColumn sx={containerStyle}>
        <FlexColumn as="form" onSubmit={handleSubmit(onSubmit)} sx={parentColumn}>
          <FlexRow sx={heading}>
            <Text.H3>Create your account</Text.H3>
          </FlexRow>
          <FormControl isInvalid={!!errors.email}>
            <FormLabel htmlFor="email">Email</FormLabel>
            <Input
              type="email"
              id="email"
              disabled={!!inviteCodeQuery}
              {...register('email', { minLength: 5, required: 'Email is required' })}
              placeholder="john.doe@example.com"
            />
            <FormErrorMessage>{errors.email && errors.email.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.password}>
            <FormLabel htmlFor="password">Password</FormLabel>
            <Input
              type="password"
              id="password"
              {...register('password', {
                minLength: {
                  value: 6,
                  message: 'Must be at least 6 characters',
                },
                required: 'Password is required',
                validate: () =>
                  getValues('password') === getValues('confirmPassword') ||
                  'Passwords do not match',
              })}
              placeholder=""
            />
            <FormErrorMessage>
              {errors.password && errors.password.message}
            </FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.confirmPassword || !!errors.password}>
            <FormLabel htmlFor="confirm password">Confirm Password</FormLabel>
            <Input
              id="confirm password"
              type="password"
              {...register('confirmPassword', {
                minLength: {
                  value: 6,
                  message: 'Must be at least 6 characters',
                },
                required: 'Please confirm your password',
                validate: (value: string) =>
                  value === getValues('password') || 'Passwords do not match',
              })}
              placeholder=""
            />
            <FormErrorMessage>
              {errors.confirmPassword && errors.confirmPassword.message}
            </FormErrorMessage>
          </FormControl>

          <FlexRow sx={inputContainer}>
            <FormControl isInvalid={!!errors.firstName}>
              <FormLabel htmlFor="first name">First Name</FormLabel>
              <Input
                id="first name"
                {...register('firstName', {
                  minLength: 2,
                  required: 'First name is required',
                  validate: (value) =>
                    validateStringValue(value, 2, 'First name is required'),
                })}
                placeholder="John"
              />
              <FormErrorMessage>
                {errors.firstName && errors.firstName.message}
              </FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.lastName}>
              <FormLabel htmlFor="last name">Last Name</FormLabel>
              <Input
                id="last name"
                {...register('lastName', {
                  minLength: 2,
                  required: 'Last name is required',
                  validate: (value) =>
                    validateStringValue(value, 2, 'Last name is required'),
                })}
                placeholder="Doe"
              />
              <FormErrorMessage>
                {errors.lastName && errors.lastName.message}
              </FormErrorMessage>
            </FormControl>
          </FlexRow>

          <FlexRow sx={inputContainer}>
            <FormControl isInvalid={!!errors.jobTitle}>
              <FormLabel htmlFor="job title">Job Title</FormLabel>
              <Input
                id="job title"
                {...register('jobTitle', {
                  minLength: 2,
                  required: 'Job title is required',
                  validate: (value) =>
                    validateStringValue(value, 2, 'Job title is required'),
                })}
                placeholder=""
              />
              <FormErrorMessage>
                {errors.jobTitle && errors.jobTitle.message}
              </FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.companyName}>
              <FormLabel htmlFor="company name">Company Name</FormLabel>
              <Input
                id="company name"
                disabled={!!inviteCodeQuery}
                {...register('companyName', {
                  minLength: 2,
                  required: 'Company name is required',
                  validate: (value) =>
                    validateStringValue(value, 2, 'Company name is required'),
                })}
                placeholder=""
              />
              <FormErrorMessage>
                {errors.companyName && errors.companyName.message}
              </FormErrorMessage>
            </FormControl>
          </FlexRow>

          <FlexRow sx={signUpContainer}>
            <Button
              variant="primary"
              type="submit"
              width="full"
              isLoading={registerInvitedUser.isLoading}
              isDisabled={registerInvitedUser.isLoading}
            >
              Sign Up
            </Button>
          </FlexRow>
        </FlexColumn>
        <FlexColumn>
          <SeparatorWithText sx={dividerStyle} text="Or sign up with:" />
          <Text sx={matchEmailTextStyle}>
            Your Microsoft account must match your account email
          </Text>
          <FlexRow sx={signUpContainer}>
            <SSOButton
              provider="microsoft"
              label={SIGN_UP_WITH_MICROSOFT_TEXT}
              onClick={handleMicrosoftSSO}
              isLoading={
                isMicrosoftLoading || registerInvitedUserWithMicrosoftSSO.isLoading
              }
              isDisabled={
                isMicrosoftLoading || registerInvitedUserWithMicrosoftSSO.isLoading
              }
            />
          </FlexRow>
        </FlexColumn>
      </FlexColumn>
    </BasePageWithTopNav>
  );
};

export default SignUp;
